const getIceServers = () => {
  const iceServers = [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' },
  ];

  const turnUrl = process.env.REACT_APP_TURN_URL;
  if (turnUrl) {
    const urls = turnUrl.split(',').map((u) => u.trim()).filter(Boolean);
    iceServers.push({
      urls,
      username: process.env.REACT_APP_TURN_USERNAME,
      credential: process.env.REACT_APP_TURN_CREDENTIAL,
    });
  }

  return iceServers;
};

const configuration = {
  iceServers: getIceServers(),
  sdpSemantics: 'unified-plan',
};

class WebRTCService {
  constructor() {
    this.peerConnection = null;
    this.localStream = null;
    this.remoteStream = null;
    this.pendingRemoteIceCandidates = [];
    this.isCaller = false;
    this.isVideoCall = false;
    this.onRemoteStreamCallback = null;
    this.onIceCandidateCallback = null;
    this.onConnectionStateChangeCallback = null;
  }

  // Initialize peer connection
  async initializePeerConnection() {
    try {
      this.peerConnection = new RTCPeerConnection(configuration);
      this.remoteStream = null;

      // Handle ICE candidates
      this.peerConnection.onicecandidate = (event) => {
        if (event.candidate && this.onIceCandidateCallback) {
          this.onIceCandidateCallback(event.candidate);
        }
      };

      // Handle remote stream
      this.peerConnection.ontrack = (event) => {
        const streamFromEvent = event?.streams?.[0] || null;
        if (streamFromEvent) {
          this.remoteStream = streamFromEvent;
        } else {
          if (!this.remoteStream) {
            this.remoteStream = new MediaStream();
          }
          if (event?.track) {
            try {
              this.remoteStream.addTrack(event.track);
            } catch (e) {
            }
          }
        }

        if (this.onRemoteStreamCallback && this.remoteStream) {
          this.onRemoteStreamCallback(this.remoteStream);
        }
      };

      // Handle connection state changes
      this.peerConnection.onconnectionstatechange = () => {
        if (!this.peerConnection) return;
        if (this.onConnectionStateChangeCallback) {
          this.onConnectionStateChangeCallback(this.peerConnection.connectionState);
        }
      };

      // Handle ICE connection state changes
      this.peerConnection.oniceconnectionstatechange = () => {
        if (!this.peerConnection) return;
      };

    } catch (error) {
      throw error;
    }
  }

  // Get local stream (audio or audio+video)
  async getLocalStream(isVideo = false) {
    try {
      this.isVideoCall = isVideo;
      const constraints = {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: isVideo ? {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          frameRate: { ideal: 30 },
          facingMode: 'user', // Front camera
        } : false,
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      this.localStream = stream;
      return stream;
    } catch (error) {
      throw error;
    }
  }

  // Add local stream to peer connection
  addLocalStreamToPeer() {
    if (!this.localStream || !this.peerConnection) {
      return;
    }

    this.localStream.getTracks().forEach((track) => {
      this.peerConnection.addTrack(track, this.localStream);
    });
  }

  // Create and send offer (caller)
  async createOffer() {
    try {
      this.isCaller = true;
      const offer = await this.peerConnection.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: this.isVideoCall,
      });

      await this.peerConnection.setLocalDescription(offer);
      return offer;
    } catch (error) {
      throw error;
    }
  }

  // Create and send answer (callee)
  async createAnswer() {
    try {
      const answer = await this.peerConnection.createAnswer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: this.isVideoCall,
      });

      await this.peerConnection.setLocalDescription(answer);
      return answer;
    } catch (error) {
      throw error;
    }
  }

  // Set remote description (offer or answer)
  async setRemoteDescription(sessionDescription) {
    try {
      if (!this.peerConnection) {
        return;
      }

      const type = sessionDescription?.type;
      const signalingState = this.peerConnection.signalingState;

      // Guard against duplicate/late answers which can occur on reconnection or duplicate socket events.
      // Caller should only apply remote answer while in have-local-offer.
      if (type === 'answer' && signalingState !== 'have-local-offer') {
        return;
      }

      const desc = new RTCSessionDescription(sessionDescription);
      await this.peerConnection.setRemoteDescription(desc);

      if (this.pendingRemoteIceCandidates.length > 0) {
        const queued = [...this.pendingRemoteIceCandidates];
        this.pendingRemoteIceCandidates = [];
        for (const candidate of queued) {
          try {
            const iceCandidate = new RTCIceCandidate(candidate);
            await this.peerConnection.addIceCandidate(iceCandidate);
          } catch (e) {
          }
        }
      }
    } catch (error) {
      throw error;
    }
  }

  // Add ICE candidate
  async addIceCandidate(candidate) {
    try {
      if (!this.peerConnection) {
        return;
      }

      if (!this.peerConnection.remoteDescription || !this.peerConnection.remoteDescription.type) {
        this.pendingRemoteIceCandidates.push(candidate);
        return;
      }

      const iceCandidate = new RTCIceCandidate(candidate);
      await this.peerConnection.addIceCandidate(iceCandidate);
    } catch (error) {
      throw error;
    }
  }

  // Mute/unmute local audio
  toggleMute() {
    if (this.localStream) {
      const audioTrack = this.localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        console.log('🔇 Audio muted:', !audioTrack.enabled);
        return !audioTrack.enabled;
      }
    }
    return false;
  }

  // Toggle video (enable/disable camera)
  toggleVideo() {
    if (this.localStream && this.isVideoCall) {
      const videoTrack = this.localStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        console.log('📹 Video disabled:', !videoTrack.enabled);
        return !videoTrack.enabled;
      }
    }
    return false;
  }

  // End call and cleanup
  endCall() {
    // Stop all tracks
    if (this.localStream) {
      this.localStream.getTracks().forEach((track) => {
        track.stop();
        console.log('⏹️ Stopped track:', track.kind);
      });
      this.localStream = null;
    }

    if (this.remoteStream) {
      this.remoteStream.getTracks().forEach((track) => {
        track.stop();
      });
      this.remoteStream = null;
    }

    // Close peer connection
    if (this.peerConnection) {
      this.peerConnection.close();
      this.peerConnection = null;
    }

    // Reset callbacks
    this.onRemoteStreamCallback = null;
    this.onIceCandidateCallback = null;
    this.onConnectionStateChangeCallback = null;
    this.isCaller = false;
  }

  // Set callbacks
  setOnRemoteStream(callback) {
    this.onRemoteStreamCallback = callback;
  }

  setOnIceCandidate(callback) {
    this.onIceCandidateCallback = callback;
  }

  setOnConnectionStateChange(callback) {
    this.onConnectionStateChangeCallback = callback;
  }

  // Get connection state
  getConnectionState() {
    if (!this.peerConnection) return 'disconnected';
    try {
      return this.peerConnection.connectionState || 'disconnected';
    } catch (error) {
      return 'disconnected';
    }
  }

  // Get ICE connection state
  getIceConnectionState() {
    if (!this.peerConnection) return 'disconnected';
    try {
      return this.peerConnection.iceConnectionState || 'disconnected';
    } catch (error) {
      return 'disconnected';
    }
  }
}

const webRTCService = new WebRTCService();
export default webRTCService;
